// A5
// SPDX-License-Identifier: Apache-2.0
// Copyright (c) A5 contributors

import type {LonLat, Cartesian} from '../core/coordinate-systems';
import {lonLatToCell, sphericalToCell, cellToSpherical} from '../core/cell';
import {fromLonLat, toCartesian, toSpherical} from '../core/coordinate-transforms';
import {cellToParent, cellToChildren, FIRST_HILBERT_RESOLUTION, MAX_RESOLUTION} from '../core/serialization';
import {compact} from '../core/compact';
import {ringWindingSign} from '../geometry/spherical-polygon';
import type {PreparedPolygon} from '../geometry/prepared-polygon';
import {preparePolygon, pointInPreparedPolygon} from '../geometry/prepared-polygon';
import {estimateCellRadius} from '../traversal/cap';
import {sampleGreatCircleArc} from '../utils/great-circle';
import {getLatticeNeighbors} from '../traversal/lattice-neighbors';
import {tripleSpaceFloodFill} from '../traversal/lattice-flood-fill';

/**
 * Maps each boundary cell to the indices of the ring segments that produced it.
 * Segment indices are global across rings (outer ring first, then holes).
 * Used by `filterBoundaryCells` to short-circuit PIP via segment-side dot products.
 */
type SegmentMap = Map<bigint, number[]>;

/**
 * Dense-sample boundary cells along every closed ring (outer + holes) at
 * `cellRadius * 0.4` spacing, calling `sphericalToCell` per sample.
 */
function denseSampleBoundary(
  rings: LonLat[][],
  ringVecsList: Cartesian[][],
  resolution: number
): {boundaryCells: bigint[]; boundarySet: Set<bigint>; segmentMap: SegmentMap} {
  const boundaryCells: bigint[] = [];
  const boundarySet = new Set<bigint>();
  const segmentMap: SegmentMap = new Map();
  const cellRadius = estimateCellRadius(resolution);
  const sampleInterval = cellRadius * 0.4;

  const recordCell = (cell: bigint, segIdx: number) => {
    if (!boundarySet.has(cell)) {
      boundarySet.add(cell);
      boundaryCells.push(cell);
    }
    const existing = segmentMap.get(cell);
    if (existing) {
      if (existing[existing.length - 1] !== segIdx) existing.push(segIdx);
    } else {
      segmentMap.set(cell, [segIdx]);
    }
  };

  let segOffset = 0;
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r];
    const ringVecs = ringVecsList[r];

    const vertexCells: bigint[] = new Array(ring.length);
    for (let i = 0; i < ring.length; i++) {
      vertexCells[i] = lonLatToCell(ring[i], resolution);
    }

    for (let i = 0; i < ring.length; i++) {
      const nextI = (i + 1) % ring.length;
      recordCell(vertexCells[i], segOffset + i);

      // Skip the lonLat round-trip: samples are authalic-Cartesian already.
      const samples = sampleGreatCircleArc(ringVecs[i], ringVecs[nextI], sampleInterval);
      for (const s of samples) {
        recordCell(sphericalToCell(toSpherical(s), resolution), segOffset + i);
      }
      recordCell(vertexCells[nextI], segOffset + i);
    }
    segOffset += ring.length;
  }

  return {boundaryCells, boundarySet, segmentMap};
}

/**
 * Filter boundary cells to those whose center is inside the polygon.
 *
 * For each cell we know which ring segment(s) sampled it. When all of those
 * segments place the cell on the interior side (cheap signed-dot test), we
 * accept immediately. When they disagree (vertex / concave corner) or the
 * cell wasn't recorded, fall back to full PIP.
 */
function filterBoundaryCells(
  boundaryCells: bigint[],
  segmentMap: SegmentMap,
  segNormals: Cartesian[],
  segSigns: number[],
  prep: PreparedPolygon
): bigint[] {
  const out: bigint[] = [];
  for (const cell of boundaryCells) {
    const cv = toCartesian(cellToSpherical(cell));
    const segments = segmentMap.get(cell);
    if (!segments) {
      if (pointInPreparedPolygon(cv, prep)) out.push(cell);
      continue;
    }
    let allInside = true;
    let anyInside = false;
    let ambiguous = false;
    for (const segIdx of segments) {
      const n = segNormals[segIdx];
      const dot = n[0] * cv[0] + n[1] * cv[1] + n[2] * cv[2];
      if (Math.abs(dot) < 1e-14) {
        ambiguous = true;
        break;
      } // on segment within float epsilon
      if (dot * segSigns[segIdx] > 0) anyInside = true;
      else allInside = false;
    }
    if (ambiguous || (anyInside && !allInside)) {
      if (pointInPreparedPolygon(cv, prep)) out.push(cell);
    } else if (allInside) {
      out.push(cell);
    }
  }
  return out;
}

/**
 * Buffer the boundary by one cell using 3-edge lattice neighbors. The shell
 * matches the connectivity of `tripleSpaceFloodFill` so the firewall (boundary
 * + exterior shell) is a tight topological barrier for the subsequent flood.
 */
function expandShell(boundaryCells: bigint[], boundarySet: Set<bigint>): bigint[] {
  const shellCells: bigint[] = [];
  const shellSet = new Set<bigint>();
  for (const cell of boundaryCells) {
    for (const neighbor of getLatticeNeighbors(cell, true)) {
      if (boundarySet.has(neighbor)) continue;
      if (!shellSet.has(neighbor)) {
        shellSet.add(neighbor);
        shellCells.push(neighbor);
      }
    }
  }
  return shellCells;
}

/**
 * Hierarchical flood fill from interior seed cells. Runs a few fine BFS layers
 * to clear the boundary, then a coarse-resolution BFS through the bulk, then
 * resumes fine BFS to fill gaps near the boundary. The coarse phase is skipped
 * when the polygon is too small to amortize its setup overhead.
 */
function floodInterior(
  interiorSeeds: bigint[],
  visited: Set<bigint>,
  boundarySize: number,
  resolution: number
): bigint[] {
  for (const cell of interiorSeeds) visited.add(cell);

  // Isoperimetric bound: B² / (4π) is the max interior for B boundary cells.
  const maxInterior = (boundarySize * boundarySize) / (4 * Math.PI);
  // res 30 has a different encoding the parent-emit optimization can't use.
  const useCoarsePhase = resolution > FIRST_HILBERT_RESOLUTION && resolution < MAX_RESOLUTION && maxInterior > 1000;

  if (!useCoarsePhase) {
    const result = tripleSpaceFloodFill(visited, interiorSeeds, resolution);
    return [...interiorSeeds, ...result.interiorCells];
  }

  const parentRes = resolution - 1;
  const coarseFirewall = new Set<bigint>();
  for (const cell of visited) coarseFirewall.add(cellToParent(cell, parentRes));

  // Phase 1: short fine BFS to move the frontier off the boundary.
  const phase1 = tripleSpaceFloodFill(visited, interiorSeeds, resolution, 3);

  // Phase 2: coarse BFS through the bulk interior.
  let coarseInteriorSet: Set<bigint> | null = null;
  const phase3Delta: bigint[] = [];
  const coarseInteriorCells: bigint[] = [];
  if (phase1.frontierCellIds.length > 0) {
    const coarseSeeds = new Set<bigint>();
    for (const cell of phase1.frontierCellIds) {
      const parent = cellToParent(cell, parentRes);
      if (!coarseFirewall.has(parent)) coarseSeeds.add(parent);
    }

    if (coarseSeeds.size > 0) {
      const coarseVisited = new Set(coarseFirewall);
      for (const seed of coarseSeeds) coarseVisited.add(seed);
      const coarseResult = tripleSpaceFloodFill(coarseVisited, [...coarseSeeds], parentRes);
      const coarseInterior = [...coarseSeeds, ...coarseResult.interiorCells];
      coarseInteriorSet = new Set(coarseInterior);
      coarseInteriorCells.push(...coarseInterior);

      // Children become firewall for phase 3; the coarse parent represents
      // them in the output, so we don't emit them individually.
      for (const coarseCell of coarseInterior) {
        for (const child of cellToChildren(coarseCell, resolution)) {
          if (!visited.has(child)) {
            visited.add(child);
            phase3Delta.push(child);
          }
        }
      }
    }
  }

  // Emit fine cells only when not already covered by a coarse parent.
  const interiorCells: bigint[] = [];
  if (coarseInteriorSet === null) {
    interiorCells.push(...interiorSeeds, ...phase1.interiorCells);
  } else {
    for (const cell of interiorSeeds) {
      if (!coarseInteriorSet.has(cellToParent(cell, parentRes))) interiorCells.push(cell);
    }
    for (const cell of phase1.interiorCells) {
      if (!coarseInteriorSet.has(cellToParent(cell, parentRes))) interiorCells.push(cell);
    }
    interiorCells.push(...coarseInteriorCells);
  }

  // Phase 3: resume fine BFS, reusing phase 1's packed state.
  const phase3 = tripleSpaceFloodFill({state: phase1.state, delta: phase3Delta}, phase1.frontierCellIds, resolution);
  interiorCells.push(...phase3.interiorCells);

  return interiorCells;
}

/**
 * How a cell is judged to belong to the polygon.
 * - `'center'`: a cell is included iff its center lies inside the polygon.
 * - `'overlapping'`: additionally include every cell that overlaps the polygon
 *   boundary, giving gap-free coverage (a superset of `'center'`).
 */
export type PolygonContainment = 'center' | 'overlapping';

type PolygonToCellsOptions = {
  /**
   * Which cells to include relative to the polygon.
   * @default 'center'
   */
  containment?: PolygonContainment;
};

/**
 * Find all cells within a polygon. The result is compacted — use `uncompact`
 * to expand to the input resolution.
 *
 * @param polygon - Either a single ring of [longitude, latitude] vertices, or
 *   GeoJSON-style rings `[outer, ...holes]` where cells inside a hole are
 *   excluded. Rings may be open or closed (GeoJSON-style, first vertex
 *   repeated at the end) — closure is automatic either way. Holes with fewer
 *   than 3 distinct vertices are ignored.
 * @param resolution - Target resolution (0..30)
 * @param options - `containment` selects `'center'` (default, cell center
 *   inside the polygon) or `'overlapping'` (any cell touching the polygon, for
 *   gap-free coverage).
 * @returns Sorted, compacted BigUint64Array of cell IDs
 */
export function polygonToCells(
  polygon: LonLat[] | LonLat[][],
  resolution: number,
  {containment = 'center'}: PolygonToCellsOptions = {}
): BigUint64Array {
  // Normalize: a flat ring is shorthand for a polygon with no holes.
  const isNested = polygon.length > 0 && typeof (polygon[0] as LonLat | LonLat[])[0] !== 'number';
  const inputRings = (isNested ? polygon : [polygon]) as LonLat[][];

  // GeoJSON rings repeat the first vertex at the end — drop the duplicate.
  const stripClosing = (ring: LonLat[]): LonLat[] => {
    const last = ring.length - 1;
    return last > 0 && ring[0][0] === ring[last][0] && ring[0][1] === ring[last][1] ? ring.slice(0, -1) : ring;
  };

  if (inputRings.length === 0) return new BigUint64Array(0);
  const outer = stripClosing(inputRings[0]);
  if (outer.length < 3) return new BigUint64Array(0);
  const rings: LonLat[][] = [outer];
  for (let r = 1; r < inputRings.length; r++) {
    const hole = stripClosing(inputRings[r]);
    if (hole.length >= 3) rings.push(hole);
  }

  // Authalic-sphere ring vectors — A5's internal sphere, so cell centers
  // compare directly with no geodetic↔authalic round-trip.
  const ringVecsList: Cartesian[][] = new Array(rings.length);
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r];
    const ringVecs: Cartesian[] = new Array(ring.length);
    for (let i = 0; i < ring.length; i++) {
      ringVecs[i] = toCartesian(fromLonLat(ring[i]));
    }
    ringVecsList[r] = ringVecs;
  }

  const prep = preparePolygon(ringVecsList);

  const {boundaryCells, boundarySet, segmentMap} = denseSampleBoundary(rings, ringVecsList, resolution);

  // The boundary contribution to the output. In 'overlapping' mode every
  // densely-sampled boundary cell contains a point on the polygon boundary, so
  // it overlaps the polygon — keep them all, unfiltered. In 'center' mode we
  // filter down to those whose center lies inside.
  let boundaryOut: bigint[];
  if (containment === 'overlapping') {
    boundaryOut = boundaryCells;
  } else {
    // Flattened per-segment normals and interior-side signs, indexed like the
    // segment map. The polygon interior lies on the *outside* of a hole ring,
    // so hole segments get the opposite sign.
    const segNormals: Cartesian[] = [];
    const segSigns: number[] = [];
    for (let r = 0; r < rings.length; r++) {
      const sign = (r === 0 ? 1 : -1) * ringWindingSign(ringVecsList[r]);
      const normals = prep.ringNormals[r];
      for (let i = 0; i < normals.length; i++) {
        segNormals.push(normals[i]);
        segSigns.push(sign);
      }
    }
    boundaryOut = filterBoundaryCells(boundaryCells, segmentMap, segNormals, segSigns, prep);
  }

  // Dense sampling can leave gaps; the shell catches them, classifying each cell.
  const shellCells = expandShell(boundaryCells, boundarySet);
  if (shellCells.length === 0) return compact(boundaryOut);

  const interiorSeeds: bigint[] = [];
  const visited = new Set(boundarySet);
  for (const cell of shellCells) {
    if (pointInPreparedPolygon(toCartesian(cellToSpherical(cell)), prep)) {
      interiorSeeds.push(cell);
    } else {
      visited.add(cell); // exterior shell (and hole interiors) join the firewall
    }
  }
  if (interiorSeeds.length === 0) return compact(boundaryOut);

  const interiorCells = floodInterior(interiorSeeds, visited, boundarySet.size, resolution);

  return compact([...boundaryOut, ...interiorCells]);
}
